import React, { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { indianBasketsData } from "@/lib/utils";
import Graph from "./trading/graph";
import { ScrollArea } from "./ui/scroll-area";

const globalApi = [11, 60, -1, -1];

const portfolioData = [
  {
    index: 0,
    icon: "/Future of Healthcare.png",
    title: "Future of Healthcare",
    description:
      "Capitalize on India's healthcare sector, focusing on pharma, medical equipment, health tech, and wellness companies.",
    basket: "domestic",
    themeColor: "orange",
    bgimage: "/explore/digital-wealth-bg.png",
    linkText: "Read more >>",
  },
  {
    index: 1,
    icon: "/Sustainable Future ESG.png",
    title: "Sustainable Future ESG",
    description:
      "Invest in companies committed to environmental responsibility, social impact, and good governance practices.",
    basket: "domestic",
    themeColor: "#45DFB1",
    bgimage: "/explore/invest-banking-bg.png",
    linkText: "Read more >>",
  },
  {
    index: 2,
    icon: "/Recession Proofers.png",
    title: "Recession Proofers",
    description:
      "Ride out economic downturns with investments in essential sectors like healthcare, consumer staples, utilities, and education.",
    themeColor: "violet",
    bgimage: "/explore/prive-wealth-bg.png",
    linkText: "Read more >>",
  },
  {
    index: 3,
    icon: "/Inflation beaters.png",
    title: "Inflation beaters",
    description:
      "Protect your wealth against rising prices by investing in companies with strong pricing power and stable cash flows.",
    themeColor: "#0dd3ff",
    bgimage: "/explore/fund-advisory-bg.png",
    linkText: "Read more >>",
  },
];

export const PortfolioGrid: React.FC = () => {
  const [activeBg, setActiveBg] = React.useState(
    "/explore/services-default.png",
  );
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);
  const [isInView, setIsInView] = React.useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedService, setSelectedService] = React.useState<any>(null);
  const navigate = useNavigate();

  // Fixed card width
  const CARD_WIDTH = 320; // Fixed width for each card
  const CARD_GAP = 24; // Gap between cards

  // Get cards to display based on screen size - exact breakpoints
  const getCardsPerView = () => {
    if (typeof window === "undefined") return 4;
    const width = window.innerWidth;

    // Calculate required width for each configuration
    // Formula: (cards * cardWidth) + ((cards - 1) * gap) + padding
    const width4Cards = 4 * CARD_WIDTH + 3 * CARD_GAP + 96; // 1472px
    const width3Cards = 3 * CARD_WIDTH + 2 * CARD_GAP + 96; // 1116px
    const width2Cards = 2 * CARD_WIDTH + 1 * CARD_GAP + 96; // 800px

    if (width >= width4Cards) return 4;
    if (width >= width3Cards) return 3;
    if (width >= width2Cards) return 2;
    return 1;
  };

  const [cardsPerView, setCardsPerView] = React.useState(getCardsPerView());

  // Calculate container width based on cards per view
  const getContainerWidth = () => {
    return cardsPerView * CARD_WIDTH + (cardsPerView - 1) * CARD_GAP;
  };
  const [isTransitioning, setIsTransitioning] = React.useState(false);

  // Create extended array for infinite scroll
  const extendedServices = [
    ...portfolioData,
    ...portfolioData,
    ...portfolioData,
  ];

  // Intersection Observer to detect if carousel is in viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setCardsPerView(getCardsPerView());
      setCurrentIndex(0);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset position when reaching end of first set
  useEffect(() => {
    if (currentIndex >= portfolioData.length && !isTransitioning) {
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(0);
      }, 50);
    }
  }, [currentIndex, isTransitioning]);

  // Auto-slide carousel - only when in view and not paused
  useEffect(() => {
    if (!isPaused && isInView) {
      const interval = setInterval(() => {
        nextSlide();
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [isPaused, isInView, currentIndex]);

  // Modal toggle
  useEffect(() => {
    if (modalOpen) {
      setIsPaused(true);
    } else {
      setIsPaused(false);
    }
  }, [modalOpen]);

  const nextSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const prevSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      if (currentIndex === 0) {
        setCurrentIndex(portfolioData.length);
        setTimeout(() => {
          setCurrentIndex(portfolioData.length - 1);
        }, 50);
      } else {
        setCurrentIndex((prev) => prev - 1);
      }
    }
  };

  return (
    <div id="explore-solution" className="scroll-mt-24" ref={sectionRef}>
      {/* Header */}
      <h6 className="mb-5 text-center mt-20 font-poppins tracking-wide leading-9 text-3xl pb-1 sm:text-3xl font-medium lg:text-3xl">
        <span className="text-white text-3xl">Nuqi</span>{" "}
        <span className="text-[#57c0af] text-3xl">
          Curated Equity Portfolios (CEP)
        </span>
      </h6>

      <p className="max-w-3xl mx-auto text-center font-poppins tracking-wide leading-6 lg:text-lg text-[#b3b3b3] mb-6">
        Our Nuqi India Basket offers a variety of curated investment themes,
        each catering to specific risk appetite and investment goals.
      </p>

      <section
        className="py-12 relative overflow-hidden transition-all duration-700"
        style={{
          backgroundImage: `url(${activeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/35 pointer-events-none"></div>
        <div className="relative z-10">
          {/* Carousel Container */}
          <div
            className="mx-auto px-6 relative"
            style={{ maxWidth: `${getContainerWidth() + 96}px` }}
          >
            {/* Navigation Buttons */}
            <button
              onClick={prevSlide}
              className="absolute -left-4 top-1/2 -translate-y-1/2 z-20 bg-[#57c0af]/20 hover:bg-[#57c0af]/40 backdrop-blur-sm border border-[#57c0af]/40 p-3 rounded-full transition-all duration-300 hover:scale-110"
              aria-label="Previous slide"
            >
              <ChevronLeft size={24} className="text-[#57c0af]" />
            </button>

            <button
              onClick={nextSlide}
              className="absolute -right-4 top-1/2 -translate-y-1/2 z-20 bg-[#57c0af]/20 hover:bg-[#57c0af]/40 backdrop-blur-sm border border-[#57c0af]/40 p-3 rounded-full transition-all duration-300 hover:scale-110"
              aria-label="Next slide"
            >
              <ChevronRight size={24} className="text-[#57c0af]" />
            </button>

            {/* Cards */}
            <div className="overflow-visible px-6">
              <div className="relative overflow-hidden">
                <div
                  className={`flex ${isTransitioning ? "transition-transform duration-500 ease-in-out" : ""}`}
                  style={{
                    transform: `translateX(-${currentIndex * (CARD_WIDTH + CARD_GAP)}px)`,
                    gap: `${CARD_GAP}px`,
                  }}
                  onTransitionEnd={() => setIsTransitioning(false)}
                >
                  {extendedServices.map((service, index) => (
                    <div
                      key={`service-${index}`}
                      onMouseEnter={() => setActiveBg(service.bgimage)}
                      onMouseLeave={() =>
                        setActiveBg("/explore/services-default.png")
                      }
                      className="group flex flex-col justify-between bg-black/50 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:translate-1 hover:scale-[1] card-hover-glow cursor-pointer flex-shrink-0 transition-all duration-300"
                      style={{
                        ["--card-theme" as any]: service.themeColor,
                        width: `${CARD_WIDTH}px`,
                        minHeight: "450px",
                      }}
                      onClick={() => {
                        setSelectedService(service);
                        setModalOpen(true);
                      }}
                    >
                      <div className="relative z-10 flex flex-col justify-between h-full">
                        <div>
                          {/* Icon */}
                          <div
                            className="mb-6 transition-transform duration-500 group-hover:scale-110 origin-left"
                            style={{ color: service.themeColor }}
                          >
                            <img
                              src={service.icon}
                              alt={service.title}
                              className="w-10 h-10"
                            />
                          </div>

                          {/* Title */}
                          <h3
                            className="text-xl font-normal mb-6 leading-tight"
                            style={{ color: service.themeColor }}
                          >
                            {service.title}
                          </h3>

                          {/* Description */}
                          {service.description && (
                            <p className="text-sm text-[#CCCCCC] font-light leading-relaxed mb-6 whitespace-pre-line group-hover:text-white transition-colors duration-300">
                              {service.description}
                            </p>
                          )}
                        </div>

                        {/* Footer Link */}
                        <div
                          className="inline-flex items-center text-sm font-bold tracking-wide mt-6 transition-all group/link cursor-pointer"
                          style={{ color: service.themeColor }}
                          onClick={() => {
                            setSelectedService(service);
                            setModalOpen(true);
                          }}
                        >
                          <span className="relative">
                            {service.linkText.replace(">>", "")}
                            <span className="absolute -bottom-1 left-0 w-0 h-px bg-current transition-all duration-300 group-hover:w-full" />
                          </span>
                          <ArrowRight
                            size={14}
                            className="ml-2 transform group-hover:translate-x-2 transition-transform duration-300"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Carousel Indicators */}
            <div className="flex justify-center gap-2 mt-8">
              {portfolioData.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsTransitioning(true);
                    setCurrentIndex(index);
                  }}
                  className={`h-2 rounded-full transition-all duration-300 ${currentIndex % portfolioData.length === index
                      ? "w-8 bg-[#57c0af]"
                      : "w-2 bg-white/30 hover:bg-white/50"
                    }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
        <style>{`
         .card-hover-glow:hover {
            border-color: var(--card-theme);
            box-shadow: 0 10px 25px -12px var(--card-theme);
         }
       `}</style>
      </section>

      {/* Modal */}
      {modalOpen && selectedService && (
        <AnimatePresence>
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Modal Content */}
            <motion.div
              className="relative max-lg:w-[90%] p-6 bg-black/50 backdrop-blur-sm border border-white/10 rounded-2xl shadow-lg"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
            >
              {/* Close Button */}
              <button
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 text-white rounded-full flex items-center justify-center hover:bg-white/30"
                onClick={() => setModalOpen(false)}
              >
                ✕
              </button>

              {/* Modal Header */}
              <h2 className="text-2xl font-bold mb-4 text-white">
                {selectedService.title}
              </h2>
              <p className="text-gray-300 mb-6 text-center">
                {selectedService.description}
              </p>

              {/* Graph Section */}
              {globalApi[selectedService.index] !== -1 && (
                <Graph id={globalApi[selectedService.index]} />
              )}

              {/* Constituents */}
              <h3 className="text-xl font-semibold mt-6 mb-4 text-white">
                Constituents of {selectedService.title}
              </h3>
              <ScrollArea className="max-h-48">
                <div className="pb-4 pr-4">
                  {indianBasketsData[selectedService.index].map(
                    (company: any) => (
                      <div
                        key={company.name}
                        className="flex justify-between items-center p-2 hover:bg-gray-700 rounded-lg cursor-pointer"
                        onClick={() => {
                          setModalOpen(false);
                          const url = `/stock?symbol=${company.symbol.replace("XNSE:", "BSE:")}`;
                          navigate(url);
                          window.location.href = url; // Forces refresh
                        }}
                      >
                        <p className="text-sm font-medium text-white">
                          {company.name}
                        </p>
                        {company.image && (
                          <img
                            src={`/scrips/${company.image}.png`}
                            alt={company.name}
                            className="w-8 h-8 object-contain"
                          />
                        )}
                      </div>
                    ),
                  )}
                </div>
              </ScrollArea>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

// import "keen-slider/keen-slider.min.css";
// import { useKeenSlider } from "keen-slider/react";
// import React, { useEffect, useState } from "react";
// import { PortfolioCard } from "./PortfolioCard"; // Ensure this import is correct

// const portfolioData = [
//   {
//     icon: "/Entrenched-enterprises-unscreen.gif",
//     title: "Future of Healthcare",
//     description:
//       "Capitalize on India's healthcare sector, focusing on pharma, medical equipment, health tech, and wellness companies..",
//     basket: "global",
//   },
//   {
//     icon: "/Income-Generators-1-unscreen.gif",
//     title: "Sustainable Future ESG",
//     description:
//       "Invest in companies committed to environmental responsibility, social impact, and good governance practices.",
//     basket: "global",
//   },
//   {
//     icon: "/Artificial-Intelligence-unscreen.gif",
//     title: "Recession Proofers",
//     description:
//       "Ride out economic downturns with investments in essential sectors like healthcare, consumer staples, utilities, and education.",
//     basket: "global",
//   },
//   {
//     icon: "/60-40-Portfolios-ETFs--unscreen.gif",
//     title: "Inflation beaters",
//     description:
//       "Protect your wealth against rising prices by investing in companies with strong pricing power and stable cash flows.",
//     basket: "global",
//   },
// ];

// export const PortfolioGrid: React.FC = () => {
//   const [sliderRef, sliderInstance] = useKeenSlider<HTMLDivElement>({
//     loop: true,
//     breakpoints: {
//       "(min-width: 768px)": {
//         slides: {
//           perView: 2,
//           spacing: 15,
//         },
//       },
//       "(min-width: 1000px)": {
//         slides: {
//           perView: 3,
//           spacing: 10,
//         },
//       },
//     },
//     slides: {
//       perView: 1,
//       spacing: 10,
//     },
//   });

//   const [isPrevDisabled, setIsPrevDisabled] = useState(true); // Disable previous button at the start
//   const [isNextDisabled, setIsNextDisabled] = useState(false); // Enable next button at the start

//   const handlePrev = () => sliderInstance.current?.prev();
//   const handleNext = () => sliderInstance.current?.next();

//   useEffect(() => {
//     if (sliderInstance.current) {
//       const updateNavigationState = () => {
//         // Ensure sliderInstance and the track details are available
//         const trackDetails = sliderInstance.current?.track?.details;
//         if (trackDetails) {
//           // Disable the previous button if we're at the first slide
//           if (trackDetails.rel === 0) {
//             setIsPrevDisabled(true);
//           } else {
//             setIsPrevDisabled(false);
//           }

//           // Disable the next button if we're at the last slide
//           if (trackDetails.rel === trackDetails.slides.length - 1) {
//             setIsNextDisabled(true);
//           } else {
//             setIsNextDisabled(false);
//           }
//         }
//       };

//       updateNavigationState(); // Initial check

//       sliderInstance.current?.on("slideChanged", updateNavigationState); // Listen for slide change
//     }
//   }, [sliderInstance]);

//   return (
//     <div className="flex flex-col items-center py-20 bg-black text-white">
//       {/* Section Header */}
//       <h6 className="mb-5 text-center mt-20 font-poppins tracking-wide leading-9 text-3xl pb-1 sm:text-3xl font-medium lg:text-3xl">
//         <span className="text-white text-3xl">Nuqi</span>{" "}
//         <span className="text-white text-3xl">Global</span>{" "}
//         <span className="text-[#57c0af] text-3xl">
//           Curated Equity Portfolios (CEP)
//         </span>
//       </h6>

//       <p className="max-w-3xl text-center font-poppins tracking-wide leading-6 lg:text-lg text-[#b3b3b3] mb-6">
//         Our Nuqi India Basket offers a variety of curated investment themes, each catering to specific risk appetite and investment goals.
//       </p>

//       {/* Carousel */}
//       <div ref={sliderRef} className="keen-slider w-full px-4 lg:px-8 py-6">
//         {portfolioData.map((portfolio, index) => (
//           <div key={index} className="keen-slider__slide">
//             <PortfolioCard
//               key={index}
//               index={index} // Pass the index to the PortfolioCard
//               {...portfolio} // Spread the portfolio data
//               onModalToggle={() => { }} // Pass an empty function for now
//             />
//           </div>
//         ))}
//       </div>

//       {/* Navigation Arrows */}
//       <div className="flex justify-center items-center gap-4 mt-4">
//         <button
//           onClick={handlePrev}
//           className={`w-14 h-14 bg-[#57c0af] rounded-full flex items-center justify-center text-black shadow-lg hover:bg-gray-200 ${isPrevDisabled ? "opacity-50 cursor-not-allowed" : ""
//             }`}
//           disabled={isPrevDisabled}
//         >
//           <span className="text-3xl font-bold">←</span> {/* Left arrow with bold and larger size */}
//         </button>
//         <button
//           onClick={handleNext}
//           className={`w-14 h-14 bg-[#57c0af] rounded-full flex items-center justify-center text-black shadow-lg hover:bg-gray-200 ${isNextDisabled ? "opacity-50 cursor-not-allowed" : ""
//             }`}
//           disabled={isNextDisabled}
//         >
//           <span className="text-3xl font-bold">→</span> {/* Right arrow with bold and larger size */}
//         </button>
//       </div>
//     </div>
//   );
// };
